import { useEffect, useMemo, useState } from 'react';

import { get, isEqual } from 'lodash-es';
import { CustomCellRendererProps } from '@ag-grid-community/react';
import { SortChangedEvent } from '@ag-grid-community/core';

import { TableItem } from './types';
import { stringComparator } from 'src/features/Dashboard/shared/utils';
import { EventServicePageNameTypes } from 'src/features/shared/ManageColumnsPopup/queries/apiTypes';
import { useUpdatePersistedSortMutation } from 'src/features/Dashboard/queries';
import { getSortedColumns } from './utils';

export function useSortedCellData(props: CustomCellRendererProps<TableItem>, columnData?: Array<string> | null) {
    const [sortOrder, setSortOrder] = useState(props.column?.getSort());

    useEffect(() => {
        if (!props.column) return;

        const onSortChange = () => {
            setSortOrder(props.column?.getSort());
        };
        props.api.addEventListener('sortChanged', onSortChange);
        onSortChange();

        return () => {
            props.api.removeEventListener('sortChanged', onSortChange);
        };
    }, [props.api, props.column]);

    // Sort the array of manufacturers present in each cell on column sort order change
    const sortedData = useMemo(() => {
        if (!columnData) return null;

        if (!sortOrder) return columnData;

        const sortedResult = [...columnData].sort(stringComparator);
        return sortOrder === 'asc' ? sortedResult : [...sortedResult.reverse()];
    }, [columnData, sortOrder]);

    return { sortOrder, sortedData };
}

// Can't use the generic useSortChange defined in /Dashboard/shared/hooks
// as it's types are from different version of the Aggrid module.
export const useSortChange = (pageName: EventServicePageNameTypes, userId: number, selectedColumnsAndFiltersQueryData: any) => {
    const { mutate: updateSortFieldMutation } = useUpdatePersistedSortMutation();

    // To support multi column sort(in future), saving columns sort state in an object of objects
    const handleSortChanged = (params: SortChangedEvent) => {
        const columnsState = params.api.getColumnState();
        if (!Array.isArray(columnsState)) return;

        const payload = getSortedColumns(columnsState);
        const apiResponse = get(selectedColumnsAndFiltersQueryData, 'table_settings[0].sort_field');

        if (!isEqual(apiResponse, payload)) {
            updateSortFieldMutation({
                pageName,
                userId,
                payload
            });
        }
    };

    return { handleSortChanged };
};
